import { z } from 'zod';

/**
 * F16 — Reservation Engine frozen API contracts (availability surface).
 *
 * See plans/16_F16_reservation_engine.md (Task 1.2). The availability surface
 * is read-only — server returns a per-day grid of slots that the consumer flow
 * renders into `@bravemove/ui-web/CalendarSlotGrid` (consumer variant) and the
 * mobile slot picker. `VenueAvailabilityService` (Wave 2B) generates the grid
 * on the fly; no materialized slots table exists.
 */

// ─── Enum constants ──────────────────────────────────────────────────────────

export const AVAILABILITY_RANGE_VALUES = ['now', 'today', 'this_weekend'] as const;
export type AvailabilityRangeKind = (typeof AVAILABILITY_RANGE_VALUES)[number];

export const SLOT_AVAILABILITY_STATUS = [
  'available',
  'booked',
  'pending',
  'blockout',
  'closed',
  'past',
  'below_min_lead',
  'beyond_max_advance',
  'no_price',
] as const;
export type SlotAvailabilityStatus = (typeof SLOT_AVAILABILITY_STATUS)[number];

// ─── Zod input schemas ───────────────────────────────────────────────────────

export const AvailabilityQueryInputSchema = z.object({
  subVenueId: z.string().uuid().optional(),
  sportName: z.string().min(1).max(40).optional(),
  dateFrom: z.string().date(),
  dateTo: z.string().date(),
  // Read from the query string, so coerce (`?partySize=4` arrives as '4').
  partySize: z.coerce.number().int().min(1).max(50).optional(),
});
export type AvailabilityQueryInput = z.infer<typeof AvailabilityQueryInputSchema>;

// ─── Read-side type aliases (server → client projections) ────────────────────

export type AvailabilitySlot = {
  readonly subVenueId: string;
  readonly subVenueName: string;
  readonly sportName: string;
  readonly startAt: string;
  readonly endAt: string;
  readonly status: SlotAvailabilityStatus;
  readonly priceAmount: number | null;
  readonly priceCurrency: 'USD' | null;
  readonly taxMode: 'inclusive' | 'exclusive' | 'none' | null;
  readonly taxRatePercent: number | null;
};

export type AvailabilityDay = {
  readonly date: string; // 'YYYY-MM-DD' in venue tz
  readonly slots: ReadonlyArray<AvailabilitySlot>;
};

export type AvailabilityResponse = {
  readonly venueId: string;
  readonly venueTimezone: string;
  readonly days: ReadonlyArray<AvailabilityDay>;
};
